import {COUNT_UNIT_LABELS} from '../constants'
import {formatInt} from './format'

/**
 * Chú thích dưới thẻ KPI "Tổng sản lượng công đoạn" (SCR-M18-001) — gọi ĐÚNG TÊN đơn vị của con số.
 *
 * ⚠️ `distinctUnits` đổi nghĩa theo `countUnit` của công đoạn: công đoạn đếm theo ĐƠN thì nó là số
 *    ĐƠN. Nhãn cứng "sản phẩm khác nhau" sai đơn vị mà KHÔNG sai số — lọc riêng Đóng thùng ngày
 *    25/09 màn hiện "1.466 sản phẩm khác nhau" trong khi 1.466 là số đơn và số sản phẩm thật là
 *    1.975. Không ai phát hiện cho tới khi đi đối chiếu với báo cáo khác.
 *
 * ⚠️ Tách ra khỏi component vì **không file test nào phủ component của ProductionPlanning**
 *    (`src/App.test.js` có import và render `App`, nhưng chỉ smoke-test "không vỡ"). Để logic này
 *    nằm trong `OutputReportPage` là để nó có mutation score 0 theo cấu tạo: mọi đột biến đều sống
 *    sót mà cả suite vẫn xanh. Đây là hàm thuần trên một mảng, không cần React để kiểm.
 */

/**
 * Đơn vị đếm có nằm trong bộ từ vựng đã biết không.
 *
 * Giá trị KHÔNG biết — thiếu hẳn field, hoặc một chuỗi ngoài hợp đồng — được đếm RIÊNG qua
 * `hasUnknown`, không gộp vào `known`: "chưa biết" là THIẾU THÔNG TIN chứ không phải một đơn vị thứ ba.
 */
const isKnownUnit = (unit) => Object.prototype.hasOwnProperty.call(COUNT_UNIT_LABELS, unit)

/**
 * Tách `countUnit` của mọi công đoạn thành hai thứ KHÁC NHAU: các đơn vị đã biết, và có hay không
 * công đoạn chưa khai.
 *
 * ⚠️ Đừng nhét "chưa biết" làm một phần tử trong cùng `Set` với đơn vị thật. Làm thế thì
 *    `{item, chưa-biết}` bị đọc thành "hai đơn vị khác nhau", và thẻ khẳng định các công đoạn không
 *    cùng đơn vị — một kết luận dữ liệu KHÔNG cho phép, vì công đoạn thiếu field rất có thể cũng là
 *    `item`. Mâu thuẫn ấy nhìn thấy ngay trên màn: cột "Đơn vị đếm" của chính dòng đó là ô TRỐNG.
 */
export const summarizeCountUnits = (groups) => {
    const children = (groups || []).reduce((all, group) => all.concat(group.children || []), [])
    const known = new Set(children.map((child) => child.countUnit).filter(isKnownUnit))

    return {known, hasUnknown: children.some((child) => !isKnownUnit(child.countUnit))}
}

export const outputCaption = (totals = {}, groups = []) => {
    /*
     * Ưu tiên số THẬT tra từ `barcodes` (`itemCount`/`orderCount`) KHI CÓ.
     *
     * ⚠️ Mọi con số suy từ action log đều đổi nghĩa theo MỨC GHI LOG của công đoạn: Đóng gói ghi
     *    một dòng mỗi KIỆN nên số suy từ log ở đó đếm kiện, không đếm sản phẩm. Hai con số tra từ
     *    `barcodes` không có bẫy đó — chúng đúng cho mọi công đoạn, nên khi có thì dùng thẳng.
     *
     * ⚠️ Hai field này CHƯA được hợp đồng API trong repo mô tả, nên nhánh dưới phải đứng vững khi
     *    chúng vắng mặt. Đường lui chỉ dùng `distinctUnits` — field mà glossary, cột bảng và màn
     *    chi tiết đều đang đọc — chứ KHÔNG dùng field nào chưa ai xác nhận.
     */
    if (totals.itemCount !== null && totals.itemCount !== undefined) {
        return `${formatInt(totals.itemCount)} sản phẩm · ${formatInt(totals.orderCount)} đơn hàng`
    }

    const units = formatInt(totals.distinctUnits)
    const {known, hasUnknown} = summarizeCountUnits(groups)

    /*
     * Trộn hai đơn vị ĐÃ BIẾT: nói thẳng là đơn vị nào trộn với đơn vị nào. Người quản lý đọc câu
     * này để biết phải bỏ bớt công đoạn nào mới có một con số đọc được — "không cùng nhau" bắt họ
     * tự đi dò cột "Đơn vị đếm" của từng dòng.
     */
    if (known.size > 1) {
        return `${units} đơn vị khác nhau — có công đoạn đếm theo đơn, có công đoạn đếm theo sản phẩm`
    }

    // Chưa khai là CHƯA BIẾT, không phải "khác đơn vị" — nói đúng thứ đang xảy ra.
    if (hasUnknown && known.size === 1) {
        return `${units} đơn vị khác nhau — có công đoạn chưa khai đơn vị đếm`
    }

    if (known.has('order')) return `${units} đơn hàng khác nhau`
    if (known.size === 1) return `${units} sản phẩm khác nhau`

    // Không công đoạn nào khai đơn vị (chưa tải xong, hoặc response thiếu hẳn `countUnit`).
    return `${units} đơn vị khác nhau`
}
